/**
 * Sidebar Navigation Component - NiceAdmin-inspired layout.
 * Attached to the left edge (no float), section headers, clean list items,
 * brand logo at top, promo card + user profile at bottom.
 * Role-aware (Premium vs Admin) and theme-aware.
 */

import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText,
  Box, Typography, Avatar, IconButton, Tooltip, Chip, Button,
} from '@mui/material';
import { Icon } from '@iconify/react';
import { useAuth } from '../../context/AuthContext';
import { useThemeMode } from '../../context/ThemeContext';

// Thin wrapper so the nav items can pass iconify names as if they were components.
const NavIcon = ({ name }) => <Icon icon={name} width={22} height={22} />;

export const DRAWER_WIDTH = 268;
export const DRAWER_WIDTH_COLLAPSED = 76;

const PREMIUM_ANALYTICS = [
  { path: '/app/dashboard', label: 'Dashboard', icon: <NavIcon name="solar:widget-2-outline" /> },
  { path: '/app/global-commodities', label: 'Commodities', icon: <NavIcon name="solar:global-outline" /> },
  { path: '/app/markets', label: 'Markets', icon: <NavIcon name="solar:shop-2-outline" /> },
  { path: '/app/currency', label: 'Currency Analysis', icon: <NavIcon name="solar:dollar-minimalistic-outline" /> },
];
const PREMIUM_ACCOUNT = [
  { path: '/app/alerts', label: 'Price Alerts', icon: <NavIcon name="solar:bell-outline" /> },
  { path: '/app/portfolio', label: 'Portfolio', icon: <NavIcon name="solar:wallet-outline" /> },
  { path: '/app/api-keys', label: 'API Keys', icon: <NavIcon name="solar:key-minimalistic-outline" /> },
  { path: '/app/subscription', label: 'Subscription', icon: <NavIcon name="solar:card-outline" /> },
];

const ADMIN_ITEMS = [
  { path: '/admin/users', label: 'User Management', icon: <NavIcon name="solar:users-group-rounded-outline" /> },
  { path: '/admin/api-config', label: 'API & Data Access', icon: <NavIcon name="solar:key-minimalistic-outline" /> },
  { path: '/admin/commodities', label: 'Commodity Management', icon: <NavIcon name="solar:box-outline" /> },
  { path: '/admin/markets', label: 'Market Management', icon: <NavIcon name="solar:shop-2-outline" /> },
  { path: '/admin/upload', label: 'Data Upload', icon: <NavIcon name="solar:cloud-upload-outline" /> },
  { path: '/admin/aliases', label: 'Alias Management', icon: <NavIcon name="solar:transfer-horizontal-outline" /> },
];

function SidebarNavItem({ item, active, collapsed, onClick, palette }) {
  // F system active state: accent chip (same treatment as the public site's
  // active range buttons and category chips).
  const activeBg = palette.accentBg;
  const activeText = palette.accent;
  const activeHoverBg = 'rgba(31,162,84,0.16)';

  const button = (
    <ListItemButton
      onClick={onClick}
      selected={active}
      sx={{
        borderRadius: '10px',
        minHeight: 44,
        minWidth: 0,
        justifyContent: collapsed ? 'center' : 'initial',
        px: collapsed ? 0 : 1.5,
        transition: 'all 0.18s ease',
        color: palette.textSecondary,
        '& .MuiListItemIcon-root': {
          color: palette.textMuted,
          transition: 'color 0.18s ease',
          minWidth: collapsed ? 0 : 36,
          mr: collapsed ? 0 : 0,
          justifyContent: 'center',
          '& svg': { fontSize: 20 },
        },
        '&:hover': {
          bgcolor: palette.surfaceHover,
          color: palette.text,
          '& .MuiListItemIcon-root': { color: palette.text },
        },
        '&.Mui-selected': {
          bgcolor: activeBg,
          color: activeText,
          '& .MuiListItemIcon-root': { color: activeText },
          '& .MuiListItemText-primary': { color: activeText, fontWeight: 600 },
          '&:hover': { bgcolor: activeHoverBg },
        },
      }}
    >
      <ListItemIcon>{item.icon}</ListItemIcon>
      {!collapsed && (
        <ListItemText
          primary={item.label}
          primaryTypographyProps={{
            fontSize: '0.875rem',
            fontWeight: active ? 600 : 500,
            noWrap: true,
          }}
        />
      )}

    </ListItemButton>
  );
  return collapsed ? <Tooltip title={item.label} placement="right" arrow>{button}</Tooltip> : button;
}

function SectionLabel({ label, collapsed, palette }) {
  if (collapsed) {
    return <Box sx={{ height: '1px', mx: 2, my: 1.5, bgcolor: palette.divider }} />;
  }
  return (
    <Typography
      variant="caption"
      sx={{
        px: 2, pt: 2, pb: 0.5, display: 'block',
        fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.12em',
        color: palette.textMuted, textTransform: 'uppercase',
      }}
    >
      {label}
    </Typography>
  );
}

function Sidebar({ open, onClose, isMobile, collapsed = false, onToggleCollapse }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isAdmin } = useAuth();
  const { palette, isDark } = useThemeMode();

  const currentWidth = collapsed && !isMobile ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH;
  const handleNavigation = (path) => { navigate(path); if (isMobile) onClose(); };
  const isActive = (path) => location.pathname === path || location.pathname.startsWith(`${path}/`);
  const handleLogout = () => { logout(); navigate('/'); };

  // No lock markers here any more: every one of these pages opens for every
  // signed-in account. What differs inside is depth and tooling, which the
  // pages say for themselves at the moment it matters.
  const primaryItems = isAdmin ? [] : PREMIUM_ANALYTICS;
  const primaryLabel = isAdmin ? null : 'Analytics';
  const secondaryItems = isAdmin ? ADMIN_ITEMS : PREMIUM_ACCOUNT;
  const secondaryLabel = isAdmin ? 'Administration' : 'My Account';

  const logoSrc = isDark ? '/farmgate-logo-reversed.svg' : '/farmgate-logo-primary.svg';

  const drawerContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', bgcolor: palette.card }}>
      {/* Brand - Farmgate logo */}
      <Box
        sx={{
          px: collapsed ? 1.5 : 2.5,
          py: 2.2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'flex-start',
          minHeight: 72,
        }}
      >
        {collapsed ? (
          // Compact mark only when collapsed
          <Box sx={{ width: 46, height: 46, borderRadius: '12px', bgcolor: '#0F2744', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width="28" height="28" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="9" y="14" width="62" height="11" rx="3" fill="#1E4D80"/>
              <rect x="9" y="23" width="13" height="50" rx="2.5" fill="#1E4D80"/>
              <rect x="58" y="23" width="13" height="50" rx="2.5" fill="#1E4D80"/>
              <circle cx="22" cy="65" r="3.5" fill="#22C55E"/>
              <circle cx="34" cy="53" r="3.5" fill="#22C55E"/>
              <circle cx="46" cy="43" r="3.5" fill="#22C55E"/>
              <circle cx="58" cy="30" r="3.5" fill="#22C55E"/>
              <path d="M22 65 Q34 49 46 43 Q52 37 58 30" stroke="#22C55E" strokeWidth="2.8" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </Box>
        ) : (
          <Box
            component="img"
            src={logoSrc}
            alt="Farmgate"
            sx={{ height: 44, width: 'auto', display: 'block' }}
          />
        )}
      </Box>

      {/* Nav - stacks directly under the brand with no scroll container */}
      <Box
        sx={{
          px: collapsed ? 0.75 : 1,
          py: 0.5,
          overflowX: 'hidden',
        }}
      >
        {primaryItems.length > 0 && primaryLabel && <SectionLabel label={primaryLabel} collapsed={collapsed} palette={palette} />}
        <List sx={{ py: 0 }}>
          {primaryItems.map((item) => (
            <ListItem key={item.path} disablePadding sx={{ py: 0.15, width: '100%' }}>
              <SidebarNavItem item={item} active={isActive(item.path)} collapsed={collapsed} onClick={() => handleNavigation(item.path)} palette={palette} isDark={isDark} />
            </ListItem>
          ))}
        </List>

        <SectionLabel label={secondaryLabel} collapsed={collapsed} palette={palette} />
        <List sx={{ py: 0 }}>
          {secondaryItems.map((item) => (
            <ListItem key={item.path} disablePadding sx={{ py: 0.15, width: '100%' }}>
              <SidebarNavItem item={item} active={isActive(item.path)} collapsed={collapsed} onClick={() => handleNavigation(item.path)} palette={palette} isDark={isDark} />
            </ListItem>
          ))}
        </List>
      </Box>

      {/* Spacer - pushes the promo/toggle/profile down to the bottom */}
      <Box sx={{ flex: 1 }} />

      {/* Promo card - "View Public Site" CTA (skips when collapsed) */}
      {!collapsed && (
        <Box sx={{ px: 2, pb: 1.5 }}>
          <Box
            sx={{
              bgcolor: palette.surfaceHover,
              border: `1px solid ${palette.cardBorder}`,
              borderRadius: '14px',
              p: 2,
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: palette.text, mb: 0.25 }}>
              Visit public site
            </Typography>
            <Typography sx={{ fontSize: '0.72rem', color: palette.textMuted, mb: 1.5, lineHeight: 1.4 }}>
              See what visitors see - markets, commodities, insights.
            </Typography>
            <Button
              onClick={() => navigate('/')}
              startIcon={<Icon icon="solar:arrow-right-up-outline" width={14} height={14} />}
              sx={{
                bgcolor: palette.accent,
                color: '#fff',
                fontWeight: 600,
                fontSize: '0.75rem',
                px: 2,
                py: 0.5,
                borderRadius: '10px',
                textTransform: 'none',
                '&:hover': { bgcolor: '#1C9550' },
              }}
            >
              Open
            </Button>
          </Box>
        </Box>
      )}

      {/* User profile + logout */}
      <Box sx={{
        p: collapsed ? 1 : 1.5,
        borderTop: `1px solid ${palette.divider}`,
        display: 'flex', alignItems: 'center',
        gap: collapsed ? 0 : 1.25,
        justifyContent: collapsed ? 'center' : 'flex-start',
      }}>
        <Avatar sx={{
          bgcolor: isAdmin ? palette.warning : '#0F2744', color: '#fff',
          width: 36, height: 36, fontSize: '0.8rem', fontWeight: 700, flexShrink: 0,
        }}>
          {user?.avatar || (user?.name ? user.name.split(' ').map((p) => p[0]).slice(0, 2).join('') : 'FG')}
        </Avatar>
        {!collapsed && (
          <>
            <Box sx={{ flex: 1, overflow: 'hidden' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography sx={{ fontWeight: 600, fontSize: '0.82rem', color: palette.text, lineHeight: 1.3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1 }}>
                  {user?.name || 'Guest'}
                </Typography>
                <Chip
                  label={isAdmin ? 'ADMIN' : (user?.plan || 'Free').toUpperCase()}
                  size="small"
                  sx={{
                    height: 16, fontSize: '0.55rem', fontWeight: 700, letterSpacing: '0.05em',
                    bgcolor: isAdmin ? palette.warningBg : palette.accentBg,
                    color: isAdmin ? palette.warning : palette.accent,
                  }}
                />
              </Box>
              <Typography sx={{ color: palette.textMuted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block', fontSize: '0.7rem' }}>
                {user?.email || ''}
              </Typography>
            </Box>
            <Tooltip title="Log out">
              <IconButton aria-label="Log out" onClick={handleLogout} size="small" sx={{ color: palette.textMuted, flexShrink: 0, '&:hover': { color: palette.danger, bgcolor: 'rgba(220,38,38,0.08)' } }}>
                <Icon icon="solar:logout-3-outline" width={18} height={18} />
              </IconButton>
            </Tooltip>
          </>
        )}
      </Box>
    </Box>
  );

  return (
    <Drawer
      variant={isMobile ? 'temporary' : 'permanent'}
      open={isMobile ? open : true}
      onClose={onClose}
      ModalProps={{ keepMounted: true }}
      sx={{
        width: currentWidth,
        flexShrink: 0,
        transition: 'width 0.25s ease',
        '& .MuiDrawer-paper': {
          width: currentWidth,
          boxSizing: 'border-box',
          borderRight: `1px solid ${palette.divider}`,
          borderTop: 'none',
          borderBottom: 'none',
          borderLeft: 'none',
          backgroundColor: palette.card,
          transition: 'width 0.25s ease',
          overflowX: 'hidden',
          overflowY: 'hidden',
          '&::-webkit-scrollbar': { display: 'none' },
          msOverflowStyle: 'none',
          scrollbarWidth: 'none',
        },
      }}
    >
      {drawerContent}
    </Drawer>
  );
}

export default Sidebar;
